/**
 * Servicio para manejar autenticación con el backend.
 */
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Crear instancia de axios para usar en toda la app
const apiClient = axios.create({
  baseURL: API_URL,
});

// Configurar axios para incluir token en todas las peticiones
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores 401 (token expirado)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authService = {
  /**
   * Registra un nuevo usuario.
   */
  async register(username, email, password) {
    try {
      const response = await apiClient.post("/auth/register", {
        username,
        email,
        password,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  /**
   * Inicia sesión y guarda el token.
   */
  async login(email, password) {
    try {
      const response = await apiClient.post("/auth/login", {
        email,
        password,
      });

      const { access_token } = response.data;

      // Guardar token en localStorage
      localStorage.setItem("token", access_token);

      // Obtener información del usuario (opcional, puedes hacer otro endpoint)
      // Por ahora, decodificamos el token para obtener el user_id
      // En producción, sería mejor tener un endpoint /auth/me

      return { token: access_token };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  /**
   * Cierra sesión.
   */
  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  /**
   * Verifica si hay un token guardado.
   */
  isAuthenticated() {
    return !!localStorage.getItem("token");
  },

  /**
   * Obtiene el token actual.
   */
  getToken() {
    return localStorage.getItem("token");
  },

  /**
   * Obtiene el usuario autenticado actual.
   */
  async getMe() {
    const response = await apiClient.get("/auth/me");
    return response.data;
  },

  /**
   * Estado público de registros de usuarios.
   */
  async getRegistrationsSetting() {
    const response = await apiClient.get("/settings/registrations");
    return response.data;
  },

  /**
   * Actualiza si se permiten nuevos registros (solo owner).
   */
  async updateRegistrationsSetting(registrationsEnabled) {
    const response = await apiClient.put("/settings/registrations", {
      registrations_enabled: registrationsEnabled,
    });
    return response.data;
  },
};

// Exportar la instancia de axios configurada para usar en otros componentes
export { apiClient };
export default axios; // Mantener exportación por defecto para compatibilidad
